import React from 'react';
import { cn } from '../../utils/cn';
import type { AvatarProps } from '../../types/components';

const avatarSizes = {
  sm: 'w-8 h-8 text-sm',
  md: 'w-10 h-10 text-base',
  lg: 'w-12 h-12 text-lg',
  xl: 'w-16 h-16 text-xl',
};

const statusColors = {
  online: 'bg-success-500',
  offline: 'bg-gray-400',
  away: 'bg-warning-500',
  busy: 'bg-error-500',
};

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      src,
      alt = '',
      size = 'md',
      fallback,
      shape = 'circle',
      status,
      className,
      testId,
      ...props
    },
    ref
  ) => {
    const [imageError, setImageError] = React.useState(false);
    const [imageLoaded, setImageLoaded] = React.useState(false);

    const sizeClasses = typeof size === 'string' 
      ? avatarSizes[size] || avatarSizes.md
      : '';

    const baseClasses = [
      'relative inline-flex items-center justify-center',
      'bg-gray-100 text-gray-600 font-medium',
      'overflow-hidden select-none',
    ];

    const shapeClasses = {
      circle: 'rounded-full',
      square: 'rounded-md',
    };

    const classes = cn(
      baseClasses,
      typeof size === 'string' ? sizeClasses : '',
      shapeClasses[shape],
      className
    );

    const style = typeof size === 'number' ? { width: size, height: size } : undefined;

    const shouldShowImage = src && !imageError && imageLoaded;
    const shouldShowFallback = !shouldShowImage && fallback;

    const getFallbackText = () => {
      if (fallback) return fallback;
      if (alt) {
        return alt
          .split(' ')
          .map(word => word.charAt(0))
          .join('')
          .toUpperCase()
          .slice(0, 2);
      }
      return '?';
    };

    return (
      <div
        ref={ref}
        className={classes}
        style={style}
        data-testid={testId}
        {...props}
      >
        {src && (
          <img
            src={src}
            alt={alt}
            className={cn(
              'w-full h-full object-cover',
              shapeClasses[shape],
              {
                'opacity-0': !imageLoaded || imageError,
                'opacity-100': imageLoaded && !imageError,
              }
            )}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        )}
        
        {(shouldShowFallback || !src) && (
          <span className="text-inherit font-medium">
            {getFallbackText()}
          </span>
        )}

        {status && (
          <span
            className={cn(
              'absolute bottom-0 right-0',
              'w-3 h-3 rounded-full border-2 border-white',
              statusColors[status]
            )}
            aria-label={`Status: ${status}`}
          />
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';